import React from 'react';

import { toast } from 'ravenjs/lib/Toaster';

import CaseEditedToast from 'components/CaseEditedToast';
import { COLOR_TOKENS } from 'constants/quartz';

const CASE_EDITED_TOAST_DURATION = 10000;

const CASE_EDITED_TOAST_STYLE = {
    background: COLOR_TOKENS.feedback.info.background,
    color: COLOR_TOKENS.feedback.info.text,
    borderLeft: `4px solid ${COLOR_TOKENS.feedback.info.border}`,
};

/**
 * Alerts currently on screen, keyed by editor name, so that further edits by the same editor
 * are bundled into their existing alert instead of opening another one.
 *
 * @type {Map<string, { changes: string[], pending: boolean, toastId: string }>}
 */
const activeAlerts = new Map();

/**
 * Edits received while the window was blurred, keyed by editor name. They are bundled the same
 * way and turned into alerts once the advisor comes back.
 *
 * @type {Map<string, string[]>}
 */
const heldBackAlerts = new Map();

function renderCaseEditedToast(editorName, changes) {
    return (
        <CaseEditedToast
            changes={changes}
            editorName={editorName}
            onReload={() => window.location.reload()}
        />
    );
}

function mergeChanges(changes, addedChanges) {
    return addedChanges.reduce(
        (merged, change) => (merged.includes(change) ? merged : [...merged, change]),
        changes
    );
}

function openAlert(editorName, changes) {
    const toastId = toast.info(renderCaseEditedToast(editorName, changes), {
        autoClose: CASE_EDITED_TOAST_DURATION,
        style: CASE_EDITED_TOAST_STYLE,
        pauseOnHover: true,
        pauseOnFocusLoss: true,
        onOpen: () => {
            const alert = activeAlerts.get(editorName);

            if (alert?.toastId === toastId) {
                activeAlerts.set(editorName, { ...alert, pending: false });
            }
        },
        onClose: () => {
            if (activeAlerts.get(editorName)?.toastId === toastId) {
                activeAlerts.delete(editorName);
            }
        },
    });

    activeAlerts.set(editorName, { changes, pending: true, toastId });
}

// An alert that has just been requested is not active yet - react-toastify only reports it as
// active once it renders - so edits arriving in the meantime still belong to it.
function isAlertOnScreen(alert) {
    return Boolean(alert) && (alert.pending || toast.isActive(alert.toastId));
}

function bundleIntoAlert(editorName, addedChanges) {
    const alert = activeAlerts.get(editorName);

    if (!isAlertOnScreen(alert)) {
        openAlert(editorName, mergeChanges([], addedChanges));
        return;
    }

    const changes = mergeChanges(alert.changes, addedChanges);

    activeAlerts.set(editorName, { ...alert, changes });

    // Passing `autoClose` restarts the timer, giving the advisor the full duration to read the
    // newly added change.
    toast.update(alert.toastId, {
        render: renderCaseEditedToast(editorName, changes),
        autoClose: CASE_EDITED_TOAST_DURATION,
    });
}

function showHeldBackAlerts() {
    window.removeEventListener('focus', showHeldBackAlerts);

    const heldBack = Array.from(heldBackAlerts.entries());

    heldBackAlerts.clear();

    heldBack.forEach(([editorName, changes]) => bundleIntoAlert(editorName, changes));
}

function holdBackAlert(editorName, change) {
    if (heldBackAlerts.size === 0) {
        window.addEventListener('focus', showHeldBackAlerts);
    }

    heldBackAlerts.set(editorName, mergeChanges(heldBackAlerts.get(editorName) || [], [change]));
}

/**
 * Alert the advisor that another user edited the case they are viewing.
 *
 * Every editor gets a single alert listing each change they made. A change reads as
 * "{area} edited" unless the backend describes the edit itself, in which case its wording is
 * listed as is - "closed this case", for example. While the alert is on screen further edits by
 * the same editor are added to it - without repeating a change already listed - and its
 * auto-close timer restarts.
 *
 * While the window is focused the alert is shown right away and react-toastify pauses its timer
 * for as long as the window stays blurred. When the window is already blurred the edits are held
 * back until the advisor returns, so the alert - whether it is a new one or the one still on
 * screen from before they left - starts from the full duration.
 *
 * @method showCaseEditedToast
 * @param  {string} editorName Display name of the user who edited the case
 * @param  {string} area       Category of the case that was edited
 * @param  {string} [message]  Backend description of the edit, listed instead of "{area} edited"
 * @return {void}
 */
export function showCaseEditedToast(editorName = 'Someone', area = 'details', message = '') {
    const editor = editorName || 'Someone';
    const change = message || `${area || 'details'} edited`;

    if (typeof document !== 'undefined' && document.hasFocus && !document.hasFocus()) {
        holdBackAlert(editor, change);
        return;
    }

    bundleIntoAlert(editor, [change]);
}
